import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import { FontFamilies, Typography } from "@/constants/Typography";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";
import {
  SafeAreaView,
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Alert,
  Platform,
  ActivityIndicator,
} from "react-native";
import SubscriptionServices from "@/services/subscriptionServices";
import Icons from "@/assets/icons";
import { Ionicons } from "@expo/vector-icons";
import { router, useFocusEffect } from "expo-router";
import { FinanceAccount } from "@/types/subscription";
import { showSuccess, showError } from "@/utils/toast";
import {StyleSheet} from "@/utils/appStyleSheet";

interface AccountItemProps {
  accountName: string;
  accountType: string;
  accountMask: string;
}

const AccountItem: React.FC<AccountItemProps> = ({
  accountName,
  accountType,
  accountMask,
}) => {
  return (
    <View style={styles.accountItem}>
      <Text style={styles.accountName}>
        {accountName} {accountType}
      </Text>
      <Text style={styles.accountMask}>**** {accountMask}</Text>
    </View>
  );
};

interface FinanceAccountSectionProps {
  financeAccount: FinanceAccount;
  onUnlink: (_id: string, _name: string) => void;
  isUnlinking: boolean;
}

const FinanceAccountSection: React.FC<FinanceAccountSectionProps> = ({
  financeAccount,
  onUnlink,
  isUnlinking,
}) => {
  return (
    <View style={styles.sectionContainer}>
      {/* Section Header - Bank Name */}
      <View style={styles.sectionHeader}>
        <View style={styles.sectionHeaderLeft}>
        <View style={styles.bankIconContainer}>
          <Icons.Bank />
        </View>
          <Text style={styles.sectionTitle}>{financeAccount.name}</Text>
        </View>
        <TouchableOpacity
          style={styles.unlinkButton}
          onPress={() => onUnlink(financeAccount.id, financeAccount.name)}
          disabled={isUnlinking}
        >
          {isUnlinking ? (
            <ActivityIndicator size="small" color="#EF4444" />
          ) : (
            <Text style={styles.unlinkButtonText}>Unlink</Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Account List */}
      <View style={styles.accountsList}>
        {financeAccount.accounts?.map((account) => (
          <AccountItem
            key={account.id}
            accountName={account.name}
            accountType={account.type}
            accountMask={account.mask}
          />
        ))}
      </View>
    </View>
  );
};

const EmptyState = () => {
  const handleLinkPlaid = () => {
    router.push("/my-money/setup");
  };

  return (
    <View style={styles.emptyContainer}>
      <Icons.BankBlack width={40} height={40} />
      <Text style={styles.emptyTitle}>No linked banks</Text>
      <Text style={styles.emptySubtitle}>
        Connect your bank accounts to start finding hidden money
      </Text>
      <TouchableOpacity style={styles.plaidButton} onPress={handleLinkPlaid}>
        <View style={styles.plaidButtonContent}>
          <Icons.LogoPlaid width={29} height={29} />
          <Text style={styles.plaidButtonText}>Link via Plaid</Text>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const AddBankButton = ({ onPress }: { onPress: () => void }) => (
  <TouchableOpacity style={styles.addBankButton} onPress={onPress}>
    <View style={styles.addBankIconContainer}>
      <Ionicons name="add" size={24} color="#0F9129" />
    </View>
    <Text style={styles.addBankText}>Add a bank</Text>
  </TouchableOpacity>
);

const LinkedBanksScreen = () => {
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

  const {
    data: financeAccounts,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["financeAccounts"],
    queryFn: SubscriptionServices.getListAccounts,
  });

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const financeAccountsList = financeAccounts?.data?.data ?? [];
  const hasAccounts = financeAccountsList.length > 0;

  const handleAddBank = () => {
    router.push("/my-money/setup");
  };

  const handleUnlinkConfirm = async (id: string) => {
    setUnlinkingId(id);
    try {
      const response = await SubscriptionServices.unlinkAccount(id);
      if (response.success) {
        showSuccess("Bank account unlinked successfully");
        refetch();
      } else {
        showError(response.message || "Failed to unlink account");
      }
    } catch {
      showError("An error occurred while unlinking the account");
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleUnlink = (id: string, name: string) => {
    if (Platform.OS === "web") {
      const result = confirm(
        `Are you sure you want to unlink ${name}? This will remove all associated accounts.`
      );
      if (result) {
        handleUnlinkConfirm(id);
      }
      return;
    }

    Alert.alert(
      "Unlink Bank Account",
      `Are you sure you want to unlink ${name}? This will remove all associated accounts.`,
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Unlink",
          style: "destructive",
          onPress: () => handleUnlinkConfirm(id),
        },
      ]
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Linked banks" />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {isLoading ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyTitle}>Loading...</Text>
          </View>
        ) : !hasAccounts ? (
          <EmptyState />
        ) : (
          <>
            {financeAccountsList.map((financeAccount) => (
              <FinanceAccountSection
                key={financeAccount.id}
                financeAccount={financeAccount}
                onUnlink={handleUnlink}
                isUnlinking={unlinkingId === financeAccount.id}
              />
            ))}
            <AddBankButton onPress={handleAddBank} />
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

export default LinkedBanksScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  contentContainer: {
    paddingBottom: 46,
  },
  // Section styles
  sectionContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    overflow: "hidden",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  sectionHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  bankIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  sectionTitle: {
    fontSize: 16,
    color: "#1F2937",
    fontFamily: FontFamilies.InterTight.SemiBold,
    flex: 1,
  },
  unlinkButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: "#FEE2E2",
    minWidth: 60,
    alignItems: "center",
  },
  unlinkButtonText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 13,
    color: "#EF4444",
  },
  // Account item styles
  accountsList: {
    paddingHorizontal: 16,
  },
  accountItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  accountName: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#1F2937",
  },
  accountMask: {
    ...Typography.caption.large,
    color: "#6B7280",
  },
  // Empty state styles
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
  },
  emptyTitle: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 20,
    color: "#161D14",
    marginBottom: 8,
    marginTop: 16,
  },
  emptySubtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    fontSize: 14,
  },
  // Add bank button styles
  addBankButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    marginTop: 8,
  },
  addBankIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#E8F5E8",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  addBankText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 14,
    color: "#0F9129",
  },
  // Plaid button styles
  plaidButton: {
    backgroundColor: "#1F2937",
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 20,
    marginTop: 24,
  },
  plaidButtonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  plaidButtonText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 14,
    color: "#FFFFFF",
  },
});
